import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {
  EntityTypes,
  EntityTypeStrings,
  ItemFieldNames,
  LinkedEntitiesModalTypes
} from "@catmodels/linked-entities/types";
import {DisplayTypes} from "@catui/components/panel/types";
import {Subscription} from "rxjs";
import {Router} from "@angular/router";
import {LinkedEntitiesService} from "@catmodels/linked-entities/services/linked-entities.service";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {LinkItem} from "@catui/types";
import {isNullOrUndefined} from "util";
import {AddDeleteEntityComponent} from "@catmodels/linked-entities/components/add-entity/add-delete-entity.component";

@Component({
  selector: 'cat-linked-entity',
  templateUrl: './linked-entity.component.html',
  styleUrls: ['./linked-entity.component.scss']
})
export class LinkedEntityComponent implements OnInit, OnDestroy {

  linkedEntitySubscription: Subscription;
  resourceLoaded: boolean = false;
  _linkedEntityUri: string;
  _linkedEntity: any;

  selectedItems = [];

  @Input() noItemPlaceholder = 'No items';
  @Input() entityType = EntityTypes.Default;
  @Input() featureLevel = true;

  @Input() set linkedEntityUri(value) {
    this._linkedEntityUri = value;
    if (!this.resourceLoaded && !isNullOrUndefined(this._linkedEntityUri)) {
      this.refreshResource();
    }
  }

  get linkedEntityUri() {
    return this._linkedEntityUri;
  }

  get linkedEntity() {
    return this._linkedEntity;
  }

  LinkedEntityTypes = EntityTypes;
  PanelDisplayTypes = DisplayTypes;

  constructor(private routerService: Router,
              private linkedEntitiesService: LinkedEntitiesService,
              private genericService: GenericResourceService,
              private modalService: ModalService) { }

  ngOnInit() {
    if (!this.resourceLoaded && !isNullOrUndefined(this._linkedEntityUri)) {
      this.refreshResource();
    }
  }

  ngOnDestroy(): void {
    if (this.linkedEntitySubscription) {
      this.linkedEntitySubscription.unsubscribe();
    }
  }

  linkedEntityChildren() {
    if (this.linkedEntity) {
      return this.linkedEntity.children;
    }

    return [];
  }

  /**
   * Callback to execute the navigation process for a manually triggered LinkItem
   * @param {LinkItem} item
   */
  navigateTo(item: LinkItem) {
    item.navigate(this.routerService);
  }

  /**
   * @returns {string}
   */
  getSetEntityLabel() {
    let label = `Set ${EntityTypeStrings[this.entityType]}`;
    if (this._linkedEntity && this._linkedEntity.onlyOneEntity === false) {
      label = label + 's';
    }
    return label;
  }

  /**
   *
   * @returns {string}
   */
  getDeleteEntityLabel() {
    return `Delete`;
  }

  /**
   * Constructing a label for a provided FeaturePort and it's index in a List
   * @returns {string}
   */
  getLabelForLinkedEntity(): string {
    if (
      !isNullOrUndefined(this._linkedEntity.label)
    ) {
      return this._linkedEntity.label;
    }

    return EntityTypeStrings[this.entityType];
  }

  /**
   * @returns {string}
   */
  getLabelForEntities(): string {
    if (
      !isNullOrUndefined(this._linkedEntity.description)
    ) {
      return this._linkedEntity.description;
    }

    return EntityTypeStrings[this.entityType];
  }

  /**
   * Open modal to edit the entity selection.
   */
  setEntityAction() {
    const compRef = this.modalService.open(AddDeleteEntityComponent);
    compRef.componentInstance.linkedEntities = this._linkedEntity;
    compRef.componentInstance.entityType = this.entityType;
    compRef.componentInstance.featureLevel = this.featureLevel;
    compRef.componentInstance.modalType = LinkedEntitiesModalTypes.Add;
    compRef.componentInstance.afterUpdate.subscribe((result) => this.setItem(result));
  }

  /**
   * Delete selected items.
   */
  deleteEntityAction() {
    const values = this._linkedEntity.children
      .filter((item) => this.selectedItems.indexOf(item) < 0)
      .map((item) => item.href);
    this.linkedEntitiesService.setEntities(
      this._linkedEntity.raw._links.self.href,
      values,
      this.entityType
    ).subscribe((result) => {
      this.setItem(result);
      this.selectedItems = [];
    });
  }

  /**
   * Currently not used. Maybe we can show data on a slide in.
   * @param value Referenced entity
   */
  openModifyDialog(value: any) {
    console.log('Clicked on entity: ', value);
  }

  /**
   * Callback from linked item list to update selected items.
   *
   * @param selectedItems
   */
  onSelectionUpdate(selectedItems: any) {
    this.selectedItems = selectedItems;
    console.log('selected items: ', this.selectedItems);
  }

  /**
   * Refresh the linked entity from the api.
   */
  private refreshResource() {
    this.resourceLoaded = true;
    if (!isNullOrUndefined(this.linkedEntityUri)) {
      if (this.linkedEntitySubscription) {
        this.linkedEntitySubscription.unsubscribe();
      }
      this.linkedEntitySubscription = this.genericService.findServiceForResource(this._linkedEntityUri)
        .loadAndGet()
        .subscribe((linkedEntity) => {
          this.setItem(linkedEntity);
        });
    }
  }

  /**
   * Update the linked entity from a LinkedEntity resource.
   *
   * @param linkedEntity The resource obtained from the api.
   */
  private setItem(linkedEntity) {
    const item = {
      label: linkedEntity['label'],
      description: linkedEntity['description'],
      raw: linkedEntity,
      children: [],
      resourceUri: linkedEntity._links.self.href,
      addDisabled: false,
      onlyOneEntity: linkedEntity.only_one_entity,
    };

    const itemFieldName = ItemFieldNames[this.entityType];
    if (!isNullOrUndefined(linkedEntity._links[itemFieldName])) {
      linkedEntity._links[itemFieldName].forEach((linkedItem) => {
        item.children.push({
          label: linkedItem.label,
          navigate: (router: Router) => () => {},
          href: linkedItem.href,
          raw: null,
        });
      });
    }

    item.addDisabled = linkedEntity.only_one_entity && item.children.length >= 1;
    this._linkedEntity = item;
    this._linkedEntityUri = item.resourceUri;
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/modules/models/linked-entities/components/linked-entity/linked-entity.component.ts